<script setup>
import Cookies from 'js-cookie';

import { defineExpose, inject, ref } from 'vue';
import { useStore } from 'vuex';
import { ContentPanel } from '@/app/components';
import { pushToDataLayer } from '@/ui/scripts/analytics/analytics.methods';

const store = useStore();
const { userGuide } = inject('config');
const focusEl = ref(null);

function closeGuide() {
  store.dispatch('projectsMap/toggleUserGuideVisibility', false);
  Cookies.set('projectMapUserGuideVisited', true);
}

function gtmProjectMapUserGuideTrigger(action) {
  pushToDataLayer(
    'projectMapUserGuide_tracking',
    'projectMapUserGuide_action',
    'projectMapUserGuide_label',
    `Click - User Guide`,
    action,
  );
}

function gtmProjectMapUserGuideCtaTrigger(url, label) {
  pushToDataLayer(
    'projectMapUserGuideCTA_tracking',
    'projectMapUserGuideCTA_action',
    'projectMapUserGuideCTA_label',
    `Click - CTA - ${label}`,
    url,
  );
}

defineExpose({
  focusEl,
});
</script>

<template>
  <ContentPanel side="modal">
    <div class="c-user-guide">
      <div class="c-user-guide__head">
        <h2 class="c-user-guide__heading" ref="focusEl" tabindex="-1">
          {{ userGuide.title }}
        </h2>
        <button
          @click="
            closeGuide();
            gtmProjectMapUserGuideTrigger('Close');
          "
          class="c-user-guide__close"
          type="button"
        >
          Close<span class="visually-hidden"> {{ userGuide.title }}</span>
          <svg aria-hidden="true" focusable="false">
            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#close"></use>
          </svg>
        </button>
        <div v-if="userGuide.desc" v-html="userGuide.desc" class="c-user-guide__desc" />
      </div>
      <div class="c-user-guide__key">
        <h3 class="c-user-guide__subtitle">{{ userGuide.subtitle }}</h3>
        <ul class="c-user-guide__list">
          <li
            v-for="item in userGuide.items"
            :class="{ 'c-user-guide__item--has-sublist': item.subItems }"
            :key="item.id"
            class="c-user-guide__item"
          >
            <div v-if="item.iconId" class="c-user-guide__icon">
              <svg
                :height="item.iconHeight"
                :width="item.iconWidth"
                aria-hidden="true"
                focusable="false"
              >
                <use xmlns:xlink="http://www.w3.org/1999/xlink" :xlink:href="item.iconId" />
              </svg>
            </div>
            <span
              :class="{ 'c-user-guide__label--has-sublist': item.subItems }"
              class="c-user-guide__label"
            >
              {{ item.label }}
            </span>
            <ul v-if="item.subItems" class="c-user-guide__sublist">
              <li v-for="subItem in item.subItems" :key="subItem.id" class="c-user-guide__item">
                <div v-if="subItem.iconId" class="c-user-guide__icon">
                  <svg
                    :height="subItem.iconHeight"
                    :width="subItem.iconWidth"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <use xmlns:xlink="http://www.w3.org/1999/xlink" :xlink:href="subItem.iconId" />
                  </svg>
                </div>
                <span class="c-user-guide__label">
                  <span class="c-user-guide__label-title">{{ subItem.label }}</span>
                  <span class="c-user-guide__label-desc" v-if="subItem.desc">{{
                    subItem.desc
                  }}</span>
                </span>
              </li>
            </ul>
          </li>
        </ul>
        <div v-if="userGuide.ctaUrl && userGuide.ctaLabel" class="c-user-guide__action">
          <a
            :href="userGuide.ctaUrl"
            @click="gtmProjectMapUserGuideCtaTrigger(userGuide.ctaUrl, userGuide.ctaLabel)"
            >{{ userGuide.ctaLabel }}</a
          >
        </div>
      </div>
    </div>
  </ContentPanel>
</template>

<style lang="scss">
@import 'sass-mq';
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/fonts';
@import '@/ui/styles/mixins/map-panels';
@import '@/ui/styles/settings/breakpoints';
@import '@/ui/styles/settings/fonts';
@import '@/ui/styles/settings/units';
@import '@/ui/styles/settings/colors';

.c-user-guide {
  @include map-panel;
  padding: 0;
}

.c-user-guide__head {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: $spacing-medium;
  padding: $spacing-large;
  background-color: $color-brand-two;
  color: $white;

  @include mq($from: large) {
    padding: $spacing-xx-large $spacing-xxxx-large;
  }
}

.c-user-guide__heading {
  @include h1-style;
  position: relative;
  padding-right: $spacing-xxxx-large;
  color: $white;
}

.c-user-guide__close {
  @include links-bold-18;
  position: absolute;
  top: $spacing-large;
  right: $spacing-large;
  z-index: 1;
  margin: 0;
  background-color: transparent;
  border: 0;
  color: $white;
  cursor: pointer;

  svg {
    margin-left: $spacing-small;
    height: rem(12px);
    width: rem(12px);
    fill: currentColor;
  }

  &:hover {
    text-decoration: underline;
  }
}

.c-user-guide__desc {
  @include body-style;
}

.c-user-guide__key {
  display: flex;
  flex-direction: column;
  gap: $spacing-large;
  padding: $spacing-large;

  @include mq($from: large) {
    padding: $spacing-xx-large $spacing-xxxx-large;
  }
}

.c-user-guide__subtitle {
  @include h2-style;
  color: $color-brand-two;
}

.c-user-guide__list,
.c-user-guide__sublist {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  width: 100%;
  list-style: none;
}

.c-user-guide__list {
  gap: $spacing-large;

  @include mq($from: large) {
    flex-direction: row;
    gap: $spacing-xxx-large;
  }
}

.c-user-guide__sublist {
  flex: 1;
  gap: $spacing-small;
}

.c-user-guide__item {
  @include body-style;
  display: flex;
  gap: $spacing-large;
  align-items: center;
  padding: 0;
  min-height: rem(36px);

  &--has-sublist {
    flex: 1;
    flex-direction: column;
    gap: $spacing-medium;
    justify-content: flex-start;
    align-items: flex-start;
  }
}

.c-user-guide__icon {
  display: flex;
  flex: 0 0 rem(50px);
  justify-content: center;
}

.c-user-guide__label {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
}

.c-user-guide__label-title {
  @include h4-style;
  color: $color-brand-two;
}

.c-user-guide__label-desc {
  font-size: rem(16px);
  font-weight: $font-weight-thin;
  line-height: (24 / 16);
}

.c-user-guide__label--has-sublist {
  @include h3-style;
  flex: 0;
  color: $color-brand-two;
}

.c-user-guide__action {
  a {
    color: $color-brand-one;
  }
}
</style>
